import { Box, Grid, GridColumn, Text } from "@modernatx-internal/ui-kit-react";
import Head from "next/head";
import React from "react";

import { CheckFormProvider } from "@/hooks/useCheckForm";
import { CountrySelectorProvider, useCountrySelector } from "@/hooks/useCountrySelector";
import { ExperienceProvider } from "@/hooks/useExperience";
import { initializeRouterState } from "@/hooks/useInitializeRouter";
import { useIsPageLoading } from "@/hooks/useIsPageLoading";
import { TextProvider, useText } from "@/hooks/useText";
import { ClientAppData } from "@/types";

import { CountrySelector } from "./CountrySelector";
import { PageFooter } from "./PageFooter";
import { PageHeader } from "./PageHeader";
import { PageTargetProvider } from "./PageTargetProvider";

interface PageLayoutProps extends ClientAppData {
  children?: React.ReactNode;
  headerVariant?: React.ComponentProps<typeof PageHeader>["variant"];
  includeCountrySelector?: boolean;
}

export const PageLayoutWithoutProviders: React.FC<PageLayoutProps> = ({
  children,
  error,
  headerVariant,
  routingStrategy
}) => {
  const { geoBlocked, notFound } = routingStrategy || {};
  const { getText } = useText();
  const { isPageLoading } = useIsPageLoading();
  const { countrySelectorVisible } = useCountrySelector();
  const mainContentVisible =
    !isPageLoading || countrySelectorVisible || initializeRouterState.isRouterInitializing;

  return (
    <>
      <Head>
        <title>{getText("META_TITLE")}</title>
        <meta name="description" content={getText("META_DESCRIPTION")} />
        <meta name="keywords" content={getText("META_KEYWORDS")} />
      </Head>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh"
        }}
      >
        <Box sx={{ flexGrow: 0 }}>
          <PageHeader variant={geoBlocked ? "minimal" : headerVariant} />
        </Box>
        <Box
          sx={{
            display: [notFound ? "flex" : "block"],
            flex: 1,
            flexDirection: "column",
            justifyContent: [notFound ? "center" : "start"],
            opacity: mainContentVisible ? 1 : 0,
            transition: "transition-color-medium"
          }}
        >
          {notFound ? (
            <Grid variant="uniform-12">
              <GridColumn
                size={[12, 12, 8]}
                sx={{
                  display: "flex",
                  flex: 1,
                  justifyContent: ["start", null, "center"],
                  py: 15,
                  textAlign: "center"
                }}
              >
                <Text as="h1" variant="title-1-medium">
                  {getText("ERROR_NOT_FOUND")}
                </Text>
              </GridColumn>
            </Grid>
          ) : (
            children
          )}
        </Box>
        <Box sx={{ flexGrow: 0 }}>
          <PageFooter />
          {error && <Box sx={{ display: "none" }} id="VeevaError">{`${error.message}`}</Box>}
        </Box>
      </Box>
    </>
  );
};

export const PageLayout: React.FC<PageLayoutProps> = (props) => (
  <ExperienceProvider experience={props.experience}>
    <TextProvider text={props.text}>
      <CountrySelectorProvider>
        <PageTargetProvider>
          <CheckFormProvider options={props.productConfiguration}>
            <PageLayoutWithoutProviders {...props} />
            {props.includeCountrySelector !== false ? <CountrySelector /> : null}
          </CheckFormProvider>
        </PageTargetProvider>
      </CountrySelectorProvider>
    </TextProvider>
  </ExperienceProvider>
);
